import { Component } from "@theme/component";

const STATS_URL = "https://api.reviews.io/stats/all";
const CACHE_TTL_MS = 60 * 60 * 1000;

/**
 * Stats requests in flight or completed, keyed by store, so every summary on the page shares one request.
 *
 * @type {Map<string, Promise<Record<string, any>>>}
 */
const statsRequests = new Map();

/**
 * @param {string} cacheKey
 * @returns {Record<string, any> | null}
 */
function readCachedStats(cacheKey) {
  try {
    const cached = JSON.parse(sessionStorage.getItem(cacheKey) ?? "null");
    return cached && Date.now() - cached.timestamp < CACHE_TTL_MS ? cached.stats : null;
  } catch {
    return null;
  }
}

/**
 * @param {string} cacheKey
 * @param {Record<string, any>} stats
 */
function writeCachedStats(cacheKey, stats) {
  try {
    sessionStorage.setItem(cacheKey, JSON.stringify({ timestamp: Date.now(), stats }));
  } catch {
    // Storage can be unavailable (e.g. private browsing); the live value is still rendered.
  }
}

/**
 * @param {string} storeKey - The public REVIEWS.io store key.
 * @returns {Promise<Record<string, any>>}
 */
function fetchStats(storeKey) {
  const existingRequest = statsRequests.get(storeKey);
  if (existingRequest) return existingRequest;

  const cacheKey = `reviews-summary:${storeKey}`;
  const cachedStats = readCachedStats(cacheKey);

  const request = cachedStats
    ? Promise.resolve(cachedStats)
    : fetch(`${STATS_URL}?store=${encodeURIComponent(storeKey)}`)
        .then((response) => {
          if (!response.ok) throw new Error(`REVIEWS.io stats request failed: ${response.status}`);
          return response.json();
        })
        .then((stats) => {
          writeCachedStats(cacheKey, stats);
          return stats;
        });

  statsRequests.set(storeKey, request);
  return request;
}

/**
 * @typedef {object} ReviewsSummaryRefs
 * @property {HTMLElement[]} [count] - Elements showing the review count.
 * @property {HTMLElement[]} [rating] - Elements showing the average rating.
 */

/**
 * Replaces the server-rendered fallback rating and review count with live REVIEWS.io values.
 * The fallback stays in place if the request fails or the store has no reviews of the chosen type.
 *
 * @extends {Component<ReviewsSummaryRefs>}
 */
class ReviewsSummaryComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#updateFromApi();
  }

  async #updateFromApi() {
    const storeKey = this.getAttribute("store-key");
    const reviewType = this.getAttribute("review-type");
    if (!storeKey || !reviewType) return;

    try {
      const stats = await fetchStats(storeKey);
      this.#render(stats[`${reviewType}_review_stats`] ?? {});
    } catch {
      // Keep the server-rendered fallback.
    }
  }

  /**
   * @param {{ total_reviews?: number | null, average_rating?: string | null }} stats
   */
  #render({ total_reviews: totalReviews, average_rating: averageRating }) {
    const total = Number(totalReviews);
    const rating = parseFloat(averageRating ?? "");

    if (!Number.isFinite(total) || total <= 0) return;

    for (const countElement of this.refs.count ?? []) {
      countElement.textContent = total.toLocaleString("en-GB");
    }

    if (!Number.isFinite(rating)) return;

    this.style.setProperty("--rating", String(rating));

    const decimals = Number(this.getAttribute("rating-decimals") ?? 1);

    for (const ratingElement of this.refs.rating ?? []) {
      ratingElement.textContent = rating.toFixed(Number.isInteger(decimals) ? decimals : 1);
    }
  }
}

if (!customElements.get("reviews-summary-component")) {
  customElements.define("reviews-summary-component", ReviewsSummaryComponent);
}
